import {
  JobApplication,
  JobApplicationStatus,
} from "../../../Entities/job-application.entity";
import { InterviewSession } from "../../../Interface/Index";
import aiInterviewService from "../../../Services/ai-interview.service";
import InterviewService from "../../../Services/interview.service";
import JobApplicationService from "../../../Services/job.application.service";

type PollingFunctions = {
  checkForNewSessions: (
    appliedJobId: string,
    interviewSessions: InterviewSession[],
    currentRound: number
  ) => Promise<InterviewSession[]>;
  checkForNextRoundQuestions: (sessionId: string) => Promise<InterviewSession>;
  checkApplicationStatus: (
    id: string,
    status: JobApplicationStatus
  ) => Promise<JobApplication>;
  checkEvaluationCompletedOrNot: (
    sessionId: string
  ) => Promise<InterviewSession>;
  checkNextRoundSession: (sessionId: string) => Promise<InterviewSession>;
};

const usePollingFunctions = (): PollingFunctions => {
  const interviewService = new InterviewService();
  const jobApplicationService = new JobApplicationService();

  const checkForNewSessions = (
    appliedJobId: string,
    interviewSessions: InterviewSession[],
    currentRound: number
  ): Promise<InterviewSession[]> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const newSessions = await interviewService.getAllInterviewSessionById(
            appliedJobId
          );

          if (newSessions && newSessions.length > interviewSessions.length) {
            clearInterval(interval);
            resolve(newSessions);
          }

          if (
            newSessions &&
            newSessions.some(
              (session) => session.interviewRoundNumber === currentRound + 1
            )
          ) {
            clearInterval(interval);
            resolve(newSessions);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkForNextRoundQuestions = (
    sessionId: string
  ): Promise<InterviewSession> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const session = await interviewService.findSessionById(sessionId);
          if (session && session.isQuestionsGenerated) {
            clearInterval(interval);
            resolve(session);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkApplicationStatus = async (
    id: string,
    status: JobApplicationStatus
  ): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.status === status) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkEvaluationCompletedOrNot = async (
    sessionId: string
  ): Promise<InterviewSession> => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 15;
      
      const interval = setInterval(async () => {
        attempts++;
        
        try {
          const session = await interviewService.findSessionById(sessionId);
          if (session && session.status !== "pending") {
            clearInterval(interval);
            resolve(session);
            return;
          }
          
          // If we've reached max attempts, reject with timeout
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            reject(new Error(`Evaluation check timeout after ${maxAttempts * 2} seconds`));
            return;
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkNextRoundSession = async (sessionId: string): Promise<InterviewSession> => {
    const maxAttempts = 15;
    let attempts = 0;
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        attempts++;
        try {
          const session = await aiInterviewService.getNextRoundSession(sessionId);
          if (session) {
            clearInterval(interval);
            resolve(session);
          }
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            reject(new Error(`Next round session check timeout after ${maxAttempts * 2} seconds`));
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });

  }

  return {
    checkForNewSessions,
    checkForNextRoundQuestions,
    checkApplicationStatus,
    checkEvaluationCompletedOrNot,
    checkNextRoundSession,
  };
};

export default usePollingFunctions;
